import {
  ExperienceAnchorType,
  SupportingActorType
} from '@dadohr/shared/domainTypes/experience';
import { camelCase } from 'lodash';
import Papa from 'papaparse';
import shortid from 'shortid';

export interface CSVRow {
  Name: string;
  title?: string;
  actor?: string;
  audience?: string;
  audience_rules?: string;
  type?: string;
  action?: string;
  Comments?: string;
  start?: string;
  deadline?: string;
  dependency?: string; // Name/title of the item this depends on
  dependency_condition?: string; // COMPLETE, START, etc.
  dependency_offset?: string;
  Status?: string;
  channel?: string;
  anchor_type?: string;
  anchor_field?: string;
  anchor_offset_duration?: string;
  anchor_offset_hour?: string;
  anchor_is_recurring?: string;
  'Text Area 1'?: string;
  'Text Area 2'?: string;
  [key: string]: any;
}

export interface CSVWarning {
  type: 'error' | 'warning';
  itemType: string; // Name column value (Task, Message, etc.)
  itemTitle: string; // title or audience column value
  rowNumber: number;
  message: string;
}

export interface ParseResult {
  data: CSVRow[];
  warnings: CSVWarning[];
}

export interface GenerateResult {
  experience: any;
  warnings: CSVWarning[];
}

// For some reason setting SA colors like this doesn't work
// Not worth the time to investigate this
// const theme = useTheme();
// const cs = theme.custom.colors;
// const actorColors = [
//   cs.blue.w300,
//   cs.green.w300,
//   cs.yellow.w300,
//   cs.red.w300,
//   cs.purple.w300,
//   cs.blue.w500,
//   cs.green.w500,
//   cs.yellow.w500,
//   cs.red.w500,
//   cs.purple.w500,
//   cs.blue.w700,
//   cs.green.w700,
//   cs.yellow.w700,
//   cs.red.w700,
//   cs.purple.w700
// ];

const actorColors = [
  '#c1defb',
  '#b3ead0',
  '#fff1b8',
  '#fdbcb4',
  '#eeddfd',
  '#0e78e4',
  '#45ba83',
  '#f6c955',
  '#e25e50',
  '#bb6bd9',
  '#0454a4',
  '#115f2c',
  '#d0a025',
  '#bf3a2b',
  '#873ea3'
];

const getActorKey = (actorName: string | undefined): string => {
  if (!actorName || actorName?.toLowerCase() === 'participant')
    return 'participant';

  return camelCase(actorName);
};

const daysToMilliseconds = (days: string | number) => {
  const numDays = typeof days === 'string' ? parseInt(days) : days;
  return !isNaN(numDays) ? numDays * 24 * 60 * 60 * 1000 : 0;
};

export function parseCSV(csvContent: string): ParseResult {
  const warnings: CSVWarning[] = [];

  const parsed = Papa.parse(csvContent, {
    header: true,
    dynamicTyping: false, // Keep as strings for our processing
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim()
  });

  if (parsed.errors && parsed.errors.length > 0) {
    parsed.errors.forEach((error) => {
      warnings.push({
        type: 'error',
        itemType: 'CSV',
        itemTitle: 'Parsing',
        rowNumber: error.row || 0,
        message: `CSV parsing error: ${error.message}`
      });
    });
  }

  return {
    data: parsed.data as CSVRow[],
    warnings
  };
}

// Simple rule parser for Phase 2
function parseAudienceRules(
  rulesString: string,
  itemType: string,
  itemTitle: string,
  rowNumber: number,
  warnings: CSVWarning[]
): any {
  if (!rulesString || rulesString.trim() === '') return 'EVERYONE';

  try {
    return parseExpression(rulesString.trim());
  } catch (error) {
    warnings.push({
      type: 'warning',
      itemType,
      itemTitle,
      rowNumber,
      message: `Could not parse audience rule: "${rulesString}". Using "EVERYONE" instead. Error: ${
        error instanceof Error ? error.message : String(error)
      }`
    });
    return 'EVERYONE';
  }
}

function parseExpression(expression: string, depth = 0): any {
  if (depth > 10) {
    throw new Error(`Maximum recursion depth exceeded parsing: ${expression}`);
  }

  expression = expression.trim();

  // Remove outer parentheses if the entire expression is wrapped
  if (
    expression.startsWith('(') &&
    expression.endsWith(')') &&
    !hasMainOperatorOutsideParens(expression)
  ) {
    expression = expression.slice(1, -1).trim();
  }

  const mainOperator = findMainOperator(expression);

  if (!mainOperator) {
    // This should be a single condition - wrap it in the expected structure
    const condition = parseCondition(expression);
    return {
      type: 'all',
      els: [condition]
    };
  }

  const parts = splitByOperator(expression, mainOperator.operator);

  const parsedParts = parts.map((part) => {
    part = part.trim();
    if (hasOperators(part)) {
      return parseExpression(part, depth + 1);
    } else {
      return parseCondition(part);
    }
  });

  return {
    type: mainOperator.operator === 'AND' ? 'all' : 'any',
    els: parsedParts
  };
}

function hasMainOperatorOutsideParens(expression: string): boolean {
  let parenDepth = 0;
  for (let i = 0; i < expression.length; i++) {
    if (expression[i] === '(') parenDepth++;
    else if (expression[i] === ')') parenDepth--;
    else if (parenDepth === 0) {
      if (
        expression.substring(i, i + 5) === ' AND ' ||
        expression.substring(i, i + 4) === ' OR '
      ) {
        return true;
      }
    }
  }
  return false;
}

function findMainOperator(
  expression: string
): { operator: string; index: number } | null {
  let parenDepth = 0;

  // Look for OR first (lower precedence) - scan entire string
  for (let i = 0; i <= expression.length - 4; i++) {
    if (expression[i] === '(') parenDepth++;
    else if (expression[i] === ')') parenDepth--;
    else if (parenDepth === 0 && expression.substring(i, i + 4) === ' OR ') {
      return { operator: 'OR', index: i };
    }
  }

  // Then look for AND (higher precedence)
  parenDepth = 0;
  for (let i = 0; i <= expression.length - 5; i++) {
    if (expression[i] === '(') parenDepth++;
    else if (expression[i] === ')') parenDepth--;
    else if (parenDepth === 0 && expression.substring(i, i + 5) === ' AND ') {
      return { operator: 'AND', index: i };
    }
  }

  return null;
}

function splitByOperator(expression: string, operator: string): string[] {
  const parts: string[] = [];
  let parenDepth = 0;
  let currentPart = '';
  const searchTerm = ` ${operator} `; // " OR " or " AND "

  let i = 0;
  while (i < expression.length) {
    if (expression[i] === '(') {
      parenDepth++;
      currentPart += expression[i];
      i++;
    } else if (expression[i] === ')') {
      parenDepth--;
      currentPart += expression[i];
      i++;
    } else if (
      parenDepth === 0 &&
      expression.substring(i, i + searchTerm.length) === searchTerm
    ) {
      // Found the operator at top level
      parts.push(currentPart.trim());
      currentPart = '';
      i += searchTerm.length; // Skip the entire operator
    } else {
      currentPart += expression[i];
      i++;
    }
  }

  // Add the last part
  if (currentPart.trim()) {
    parts.push(currentPart.trim());
  }

  return parts;
}

function hasOperators(expression: string): boolean {
  return expression.includes(' AND ') || expression.includes(' OR ');
}

function parseCondition(condition: string): any {
  condition = condition.trim();

  // If this still has operators, something went wrong
  if (hasOperators(condition)) {
    throw new Error(`Condition still has operators: ${condition}`);
  }

  // Check for isBlank and isNotBlank first (no value needed)
  if (condition.includes(' isBlank')) {
    const fieldPart = condition.replace(' isBlank', '').trim();
    return { op: 'isBlank', k: fieldPart, v: null };
  }

  if (condition.includes(' isNotBlank')) {
    const fieldPart = condition.replace(' isNotBlank', '').trim();
    return { op: 'isNotBlank', k: fieldPart, v: null };
  }

  const operators = ['>=', '<=', '!=', '==', '=~', '!~', '>', '<'];

  for (const op of operators) {
    const index = condition.indexOf(op);
    if (index > 0) {
      const fieldPart = condition.substring(0, index).trim();
      const valuePart = condition.substring(index + op.length).trim();

      // Convert string values to appropriate types
      let parsedValue: any = valuePart;
      if (valuePart === 'true') parsedValue = true;
      else if (valuePart === 'false') parsedValue = false;
      else if (!isNaN(Number(valuePart)) && valuePart !== '')
        parsedValue = Number(valuePart);

      return { op, k: fieldPart, v: parsedValue };
    }
  }

  throw new Error(`Could not parse condition: ${condition}`);
}

export function generateExperienceFromCSV(
  existingExperience: any,
  csvData: CSVRow[],
  overwrite?: boolean
): GenerateResult {
  const warnings: CSVWarning[] = [];

  // Add row numbers to data for better error reporting
  const csvDataWithRows = csvData.map((row, index) => ({
    ...row,
    _rowNumber: index + 2
  })); // +2 because CSV has header row and is 1-indexed

  const relevantRows = csvDataWithRows.filter(
    (row) =>
      row.Name === 'Task' ||
      row.Name === 'Message' ||
      row.Name === 'Automation' ||
      row.Name === 'Audience' ||
      row.Name === 'Anchor' ||
      row.Name?.includes('Swim Lane')
  );

  const newAnchor = extractAnchor(
    relevantRows,
    existingExperience.anchor,
    warnings
  );

  const audienceDefinitions = extractAudienceDefinitions(
    relevantRows,
    warnings
  );

  const newSupportingActors = extractSupportingActors(
    existingExperience,
    relevantRows,
    overwrite,
    warnings
  );

  const offsetDuration = newAnchor.offset.duration || 0;
  const { tasks: newTasks, itemIdMap } = extractTasksWithDependencies(
    relevantRows,
    audienceDefinitions,
    offsetDuration,
    warnings
  );

  const newMessages = extractMessagesWithDependencies(
    relevantRows,
    audienceDefinitions,
    offsetDuration,
    itemIdMap,
    warnings
  );

  const newAutomations = extractAutomationsWithDependencies(
    relevantRows,
    audienceDefinitions,
    offsetDuration,
    itemIdMap,
    warnings
  );

  const experience = {
    ...existingExperience,
    anchor: newAnchor,
    supportingActors: newSupportingActors,
    tasks: overwrite ? newTasks : [...existingExperience.tasks, ...newTasks],
    messages: overwrite
      ? { intro: null, outro: null, other: newMessages }
      : {
          ...existingExperience.messages,
          other: [...existingExperience.messages.other, ...newMessages]
        },
    automations: overwrite
      ? newAutomations
      : [...existingExperience.automations, ...newAutomations],
    updatedAt: new Date()
  };

  return { experience, warnings };
}

function extractAnchor(
  relevantRows: (CSVRow & { _rowNumber: number })[],
  existingAnchor: any,
  warnings: CSVWarning[]
): any {
  const anchorRow = relevantRows.find((row) => row.Name === 'Anchor');

  if (!anchorRow) {
    return existingAnchor; // Keep existing anchor if no anchor row found
  }

  const anchorType = anchorRow.anchor_type || ExperienceAnchorType.IMMEDIATE;
  const offsetHour = parseInt(anchorRow.anchor_offset_hour || '9');
  const offsetDuration = anchorRow.anchor_offset_duration
    ? daysToMilliseconds(parseInt(anchorRow.anchor_offset_duration))
    : 0;

  const baseAnchor = {
    autoEnrollmentRejectionThreshold: 0,
    autoEnrollmentRejectionEnabled: false,
    offset: {
      type: 'ABSOLUTE',
      duration: offsetDuration,
      hour: offsetHour
    },
    v: 2
  };

  switch (anchorType.toUpperCase()) {
    case 'IMMEDIATE':
      return {
        ...baseAnchor,
        type: ExperienceAnchorType.IMMEDIATE,
        autoEnrollmentRejectionThreshold: null
      };

    case 'EMPLOYEE_DATE':
      return {
        ...baseAnchor,
        type: ExperienceAnchorType.EMPLOYEE_DATE,
        field: anchorRow.anchor_field || 'startDate',
        isRecurring: anchorRow.anchor_is_recurring === 'true'
      };

    // case 'FIXED':
    //   return {
    //     ...baseAnchor,
    //     type: ExperienceAnchorType.FIXED,
    //     ts: new Date() // Would need a specific timestamp field in CSV
    //   };

    default:
      warnings.push({
        type: 'warning',
        itemType: 'Anchor',
        itemTitle: anchorRow.title || 'Anchor Settings',
        rowNumber: anchorRow._rowNumber,
        message: `Unknown anchor type: "${anchorType}". Defaulting to IMMEDIATE.`
      });
      return {
        ...baseAnchor,
        type: ExperienceAnchorType.IMMEDIATE,
        autoEnrollmentRejectionThreshold: null
      };
  }
}

function extractAudienceDefinitions(
  relevantRows: (CSVRow & { _rowNumber: number })[],
  warnings: CSVWarning[]
): Map<string, { name: string; rule: any; color: string }> {
  const audienceDefinitions = new Map();
  const audienceColors = [
    '#f6c955',
    '#55c5f6',
    '#f655c5',
    '#55f6c5',
    '#c555f6'
  ];
  let audienceColorIndex = 0;

  const audienceRows = relevantRows.filter((row) => row.Name === 'Audience');
  audienceRows.forEach((row) => {
    if (row.title) {
      audienceDefinitions.set(row.title, {
        name: row.title,
        rule: row.audience_rules
          ? parseAudienceRules(
              row.audience_rules,
              'Audience',
              row.title,
              row._rowNumber,
              warnings
            )
          : 'EVERYONE',
        color: audienceColors[audienceColorIndex++ % audienceColors.length]
      });
    } else {
      warnings.push({
        type: 'warning',
        itemType: 'Audience',
        itemTitle: 'Unnamed Audience',
        rowNumber: row._rowNumber,
        message:
          'Audience row missing title field. This audience will be skipped.'
      });
    }
  });

  return audienceDefinitions;
}

function createAudienceFromDefinition(
  audienceName: string | undefined,
  itemIndex: number,
  audienceDefinitions: Map<string, any>
): any[] {
  const audienceColors = [
    '#f6c955',
    '#55c5f6',
    '#f655c5',
    '#55f6c5',
    '#c555f6'
  ];

  const audiences = [];

  // Subtask IDs are used in slack and influence the character limit
  // Is this why we use shortID
  // Form subtask could be useful for slack and trigger a modal to solve character limit problems
  // We should validate characters on our end for slack

  // If the audience has a value we split it by semi-colon to allow mutliple audiences and add them
  // Audiences must be defined in the audience definitions to be valid
  if (audienceName && audienceName.trim() !== '') {
    const potentialAudiences = audienceName.split(';');
    potentialAudiences.forEach((audience) => {
      if (audience && audience.trim() !== '') {
        const definition = audienceDefinitions.get(audience.trim());
        if (definition) {
          audiences.push({
            id: shortid(),
            name: definition.name,
            rule: definition.rule,
            color: definition.color,
            blocks: []
          });
        }
      }
    });
  }

  // Always add the default "Everyone" audience last
  audiences.push({
    id: shortid(),
    name: '',
    rule: 'EVERYONE',
    color: '#cccccc',
    blocks: []
  });

  return audiences;
}

function extractSupportingActors(
  existingExperience: any,
  relevantRows: (CSVRow & { _rowNumber: number })[],
  overwrite?: boolean,
  warnings?: CSVWarning[]
): { [key: string]: any } {
  const newSupportingActors: { [key: string]: any } = overwrite
    ? {}
    : { ...existingExperience.supportingActors };

  const swimLaneRows = relevantRows.filter((row) =>
    row.Name?.includes('Swim Lane')
  );
  let colorIndex = 0;

  swimLaneRows.forEach((row) => {
    const actorName = row['Text Area 2'];
    if (actorName && actorName.toLowerCase() !== 'participant') {
      const actorKey = getActorKey(actorName);
      if (!newSupportingActors[actorKey]) {
        newSupportingActors[actorKey] = {
          type: SupportingActorType.ASSIGNED_BY_SUBTASK,
          name: actorName,
          key: actorKey,
          defaultUserId: '',
          ruleBasedUserIds: [],
          color: actorColors[colorIndex++ % actorColors.length],
          ignoreWhenMissing: false
        };
      }
    }
  });

  const itemRows = relevantRows.filter(
    (row) =>
      row.Name === 'Task' || row.Name === 'Message' || row.Name === 'Automation'
  );
  itemRows.forEach((row) => {
    const actorName = row.actor;
    if (actorName && actorName.toLowerCase() !== 'participant') {
      const actorKey = getActorKey(actorName);
      if (!newSupportingActors[actorKey]) {
        newSupportingActors[actorKey] = {
          type: SupportingActorType.ASSIGNED_BY_SUBTASK,
          name: actorName,
          key: actorKey,
          defaultUserId: '',
          ruleBasedUserIds: [],
          color: actorColors[colorIndex++ % actorColors.length],
          ignoreWhenMissing: false
        };
      }
    }
  });

  return newSupportingActors;
}

function extractTasksWithDependencies(
  relevantRows: (CSVRow & { _rowNumber: number })[],
  audienceDefinitions: Map<string, any>,
  offsetDuration: number,
  warnings: CSVWarning[]
): { tasks: any[]; itemIdMap: Map<string, string> } {
  const taskRows = relevantRows.filter((row) => row.Name === 'Task');
  const itemIdMap = new Map<string, string>(); // title -> id mapping

  // First pass: Create all tasks and build ID mapping
  const tasks = taskRows.map((row, index) => {
    const taskId = shortid();
    const audiences = createAudienceFromDefinition(
      row.audience,
      index,
      audienceDefinitions
    );

    if (row.title) {
      itemIdMap.set(row.title, taskId);
    }

    const hasDependency = row.dependency && row.dependency.trim() !== '';
    const startTime = hasDependency
      ? 0
      : row.start
      ? daysToMilliseconds(parseInt(row.start)) + Math.abs(offsetDuration)
      : Math.abs(offsetDuration);

    const task: any = {
      id: taskId,
      title: row.title || 'Untitled Task',
      description: { t: '', v: 1 },
      actor: getActorKey(row.actor),
      start: startTime,
      end:
        row.deadline && !hasDependency
          ? daysToMilliseconds(
              parseInt(row.deadline) + parseInt(row.start || '0')
            ) + Math.abs(offsetDuration)
          : 0,
      timing: hasDependency
        ? {
            dependentOn: [] as any[], // Will be filled in second pass
            end: {
              type: 'relativeToDeadlineOfTrigger',
              duration: row.dependency_offset
                ? daysToMilliseconds(parseInt(row.dependency_offset))
                : 0
            },
            type: 'triggered'
          }
        : {
            start: startTime,
            end: row.deadline
              ? daysToMilliseconds(
                  parseInt(row.deadline) + parseInt(row.start || '0')
                ) + Math.abs(offsetDuration)
              : 0,
            type: 'fixed',
            time: null
          },
      preferUserTimezone: false,
      startTime: null,
      subTasks: [],
      blocks: [
        {
          data: {
            label: 'OK'
          },
          hooks: {},
          id: shortid(),
          parentLayoutElement: null,
          requirementType: 'MANDATORY',
          type: 'CONFIRM'
        }
      ],
      reminders: [],
      remindersV2: [],
      customCompletionCriteria: [],
      audiences: audiences.map((audience) => ({
        ...audience,
        completionCriteria: []
      })),
      remindUntilDone: null,
      channels: [
        {
          type: 'EMAIL',
          emailFrom: 'email'
        }
      ],
      customSender: null,
      batchMode: 'DEFAULT',
      catchup: { disabled: false },
      v: 1,
      _csvRow: row // Store original row for second pass
    };

    return task;
  });

  // Second pass: Resolve dependencies
  tasks.forEach((task) => {
    const row = task._csvRow;
    if (row.dependency && row.dependency.trim() !== '') {
      const dependentItemId = itemIdMap.get(row.dependency);

      if (dependentItemId && task.timing.type === 'triggered') {
        task.timing.dependentOn = [
          {
            condition: row.dependency_condition || 'COMPLETE',
            itemId: dependentItemId,
            itemType: 'TASK'
          }
        ];
        task.timing.end = {
          duration: daysToMilliseconds(row.deadline || '0'),
          type: 'relativeToDeadlineOfTrigger'
        };
      } else if (!dependentItemId) {
        warnings.push({
          type: 'warning',
          itemType: 'Task',
          itemTitle: task.title,
          rowNumber: row._rowNumber,
          message: `Dependency "${row.dependency}" not found. Task will use fixed timing instead.`
        });
        // Fall back to fixed timing if dependency not found
        const startTime = row.start
          ? daysToMilliseconds(parseInt(row.start)) + Math.abs(offsetDuration)
          : Math.abs(offsetDuration);
        task.timing = {
          start: startTime,
          end: row.deadline
            ? daysToMilliseconds(
                parseInt(row.deadline) + parseInt(row.start || '0')
              ) + Math.abs(offsetDuration)
            : 0,
          type: 'fixed',
          time: null
        };
      }
    }

    // Clean up temporary data
    delete task._csvRow;
  });

  return { tasks, itemIdMap };
}

function extractMessagesWithDependencies(
  relevantRows: (CSVRow & { _rowNumber: number })[],
  audienceDefinitions: Map<string, any>,
  offsetDuration: number,
  itemIdMap: Map<string, string>,
  warnings: CSVWarning[]
): any[] {
  return relevantRows
    .filter((row) => row.Name === 'Message')
    .map((row, index) => {
      const hasDependency = row.dependency && row.dependency.trim() !== '';
      const startTime = hasDependency
        ? 0
        : row.start
        ? daysToMilliseconds(parseInt(row.start)) + Math.abs(offsetDuration)
        : Math.abs(offsetDuration);

      const message: any = {
        id: shortid(),
        actor: getActorKey(row.actor),
        subject: row.title || 'Untitled Message',
        body: { t: '', v: 1 },
        blocks: [
          {
            id: shortid(),
            type: 'TEXT',
            parentLayoutElement: null,
            data: {
              t: { t: '', v: 1 }
            }
          }
        ],
        channels: [
          {
            type: row.channel?.toUpperCase() || 'EMAIL',
            emailFrom: 'email'
          }
        ],
        audiences: createAudienceFromDefinition(
          row.audience,
          index,
          audienceDefinitions
        ),
        taskList: {
          range: 'ALL',
          variant: 'expanded'
        },
        sendAt: startTime,
        startTime: null,
        timing: hasDependency
          ? {
              dependentOn: [] as any[],
              end: {
                type: 'relativeToDeadlineOfTrigger',
                duration: row.dependency_offset
                  ? daysToMilliseconds(parseInt(row.dependency_offset))
                  : 0
              },
              type: 'triggered'
            }
          : {
              start: startTime,
              type: 'fixed',
              time: null
            },
        preferUserTimezone: false,
        batchMode: 'DEFAULT',
        customSender: null,
        catchup: { disabled: false }
      };

      // Resolve dependency if present
      if (hasDependency) {
        const dependentItemId = itemIdMap.get(row.dependency!);
        if (dependentItemId && message.timing.type === 'triggered') {
          message.timing.dependentOn = [
            {
              condition: row.dependency_condition || 'COMPLETE',
              itemId: dependentItemId,
              itemType: 'TASK'
            }
          ];
        } else if (!dependentItemId) {
          warnings.push({
            type: 'warning',
            itemType: 'Message',
            itemTitle: message.subject,
            rowNumber: row._rowNumber,
            message: `Dependency "${row.dependency}" not found. Message will use fixed timing instead.`
          });
          // Fall back to fixed timing
          message.timing = {
            start: row.start
              ? daysToMilliseconds(parseInt(row.start)) +
                Math.abs(offsetDuration)
              : Math.abs(offsetDuration),
            type: 'fixed',
            time: null
          };
        }
      }

      return message;
    });
}

function extractAutomationsWithDependencies(
  relevantRows: (CSVRow & { _rowNumber: number })[],
  audienceDefinitions: Map<string, any>,
  offsetDuration: number,
  itemIdMap: Map<string, string>,
  warnings: CSVWarning[]
): any[] {
  return relevantRows
    .filter((row) => row.Name === 'Automation')
    .map((row, index) => {
      const hasDependency = row.dependency && row.dependency.trim() !== '';
      const startTime = hasDependency
        ? 0
        : row.start
        ? daysToMilliseconds(parseInt(row.start)) + Math.abs(offsetDuration)
        : Math.abs(offsetDuration);

      const automation: any = {
        id: shortid(),
        title: row.title || 'Untitled Automation',
        description: { t: '', v: 1 },
        actions: [],
        audiences: createAudienceFromDefinition(
          row.audience,
          index,
          audienceDefinitions
        ),
        at: null, // deprecated
        timing: hasDependency
          ? {
              dependentOn: [] as any[],
              end: {
                type: 'relativeToDeadlineOfTrigger',
                duration: row.dependency_offset
                  ? daysToMilliseconds(parseInt(row.dependency_offset))
                  : 0
              },
              type: 'triggered'
            }
          : {
              start: startTime,
              type: 'fixed',
              time: null
            },
        catchup: { disabled: false }
      };

      // Resolve dependency if present
      if (hasDependency) {
        const dependentItemId = itemIdMap.get(row.dependency!);
        if (dependentItemId && automation.timing.type === 'triggered') {
          automation.timing.dependentOn = [
            {
              condition: row.dependency_condition || 'COMPLETE',
              itemId: dependentItemId,
              itemType: 'TASK'
            }
          ];
        } else if (!dependentItemId) {
          warnings.push({
            type: 'warning',
            itemType: 'Automation',
            itemTitle: automation.title,
            rowNumber: row._rowNumber,
            message: `Dependency "${row.dependency}" not found. Automation will use fixed timing instead.`
          });
          // Fall back to fixed timing
          automation.timing = {
            start: row.start
              ? daysToMilliseconds(parseInt(row.start)) +
                Math.abs(offsetDuration)
              : Math.abs(offsetDuration),
            type: 'fixed',
            time: null
          };
        }
      }

      return automation;
    });
}
